import type { FC } from "react";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useBrandKit } from "../../brand-kit/hooks/use-brand-kit";
import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import { CeCheckbox } from "../../generic/components/form-fields/ce-checkbox";
import { CeColourField } from "../../generic/components/form-fields/ce-colour-field";
import { CeSlider } from "../../generic/components/form-fields/ce-slider";
import { CeToggle } from "../../generic/components/form-fields/ce-toggle";
import { FormStack } from "../../generic/components/form-fields/form-stack";

export const HeaderSettingsEditor: FC = () => {
  const { branding } = useBrandKit();

  const methods = useFormContext<EmailDesignSchema>();

  const handleToggleLogo = useCallback(
    (checked: boolean): void =>
      methods.setValue(
        "header.logo",
        checked
          ? { src: branding?.logo?.src || "", align: "center", proportionOfMaxWidth: 1, version: 2 }
          : null,
      ),
    [methods, branding],
  );

  const handleToggleText = useCallback(
    (checked: boolean): void =>
      methods.setValue(
        "header.text",
        checked
          ? {
              align: "center",
              value: "",
              color: branding?.color?.hex || "#000000",
            }
          : null,
      ),
    [methods, branding],
  );

  return (
    <FormStack>
      <CeCheckbox<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.fullWidth"
        label="Full Width"
      />
      <CeColourField<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.backgroundColor"
        label="Background Color"
      />
      <CeToggle
        onChange={handleToggleLogo}
        analyticsCategory="email_design"
        name="header.logo"
        label="Use Logo"
      />
      <CeToggle
        onChange={handleToggleText}
        analyticsCategory="email_design"
        name="header.text"
        label="Use Header Text"
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.paper_padding_side"
        label="Side Padding"
        min={0}
        max={25}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.paper_padding_top"
        label="Top Padding"
        min={0}
        max={25}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.paper_padding_bottom"
        label="Bottom Padding"
        min={0}
        max={25}
        step={1}
      />
      <CeCheckbox<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.hide_image_url_from_notification"
        label="Disable Image URL in Desktop Email Notifications"
      />
    </FormStack>
  );
};
